import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CrawlerProvider, useCrawlers } from "@/context/CrawlerContext";
import GlobalSearch from "@/components/GlobalSearch";
import { Button } from "@/components/ui/button";
import { Scale, LogOut, LayoutDashboard, FileStack, ClipboardCheck, History, Loader2 } from "lucide-react";

const MODES = [
  { to: "/dashboard", label: "Crawler-Dashboard", Icon: LayoutDashboard, testId: "mode-dashboard" },
  { to: "/hefte", label: "PP (Pre Processing)", Icon: FileStack, testId: "mode-hefte" },
  { to: "/ok", label: "OK", Icon: ClipboardCheck, testId: "mode-ok" },
  { to: "/protokoll", label: "Hefte Heute", Icon: History, testId: "mode-protokoll" },
];

const navCls = ({ isActive }) =>
  `inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
    isActive ? "bg-[#0F4C81] text-white shadow-sm" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
  }`;

function QueueMini() {
  const { busy, queue, runAllTotal, doneCount, progressPct } = useCrawlers();
  if (!busy) return null;
  return (
    <div className="flex items-center gap-2.5 text-xs text-gray-600 bg-gray-100 border border-gray-200 rounded-md px-2.5 py-1.5 min-w-[200px]" data-testid="queue-status">
      <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-600 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate">
            {queue.running ? <>läuft: <span className="font-mono font-semibold text-[#0F4C81]">{queue.running}</span></> : "Warteschlange…"}
          </span>
          {runAllTotal > 1 && <span className="font-semibold text-gray-500 shrink-0" data-testid="queue-progress-count">{doneCount}/{runAllTotal}</span>}
        </div>
        {runAllTotal > 1 && (
          <div className="mt-1 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden" data-testid="queue-progress-bar">
            <div className="h-full bg-[#0F4C81] rounded-full transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
        )}
      </div>
    </div>
  );
}

function HeaderBar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onSearchSelect = (res, hit) => {
    navigate("/hefte", { state: { openDoc: res.doc_id, jumpPage: hit?.page || 0, ts: Date.now() } });
  };
  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <header className="shrink-0 bg-white border-b border-gray-200 h-16 flex items-center px-3 sm:px-5 gap-2 sm:gap-4">
      <NavLink to="/" className="flex items-center gap-2.5 shrink-0" data-testid="app-logo">
        <div className="h-8 w-8 rounded bg-[#0F4C81] flex items-center justify-center">
          <Scale className="h-4 w-4 text-white" strokeWidth={2} />
        </div>
        <div className="leading-tight hidden sm:block">
          <div className="font-heading font-bold text-gray-900 text-sm tracking-tight">makrolog</div>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold">Rechts-OCR</div>
        </div>
      </NavLink>

      <nav className="flex items-center gap-0.5 sm:gap-1 shrink-0" data-testid="mode-switcher">
        {MODES.map(({ to, label, Icon, testId }) => (
          <NavLink key={to} to={to} className={navCls} data-testid={testId} title={label}>
            <Icon className="h-4 w-4" /> <span className="hidden lg:inline">{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="flex-1 flex items-center justify-end gap-3 min-w-0">
        <div className="hidden md:block w-full max-w-xs"><GlobalSearch onSelect={onSearchSelect} /></div>
        <div className="hidden md:block"><QueueMini /></div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 shrink-0">
        <div className="text-right hidden xl:block">
          <div className="text-sm font-medium text-gray-900" data-testid="user-name">{user?.name || "—"}</div>
          <div className="text-xs text-gray-500" data-testid="user-email">{user?.email}</div>
        </div>
        {user?.picture ? (
          <img src={user.picture} alt="" className="h-9 w-9 rounded-full border border-gray-200 hidden sm:block" />
        ) : (
          <div className="h-9 w-9 rounded-full bg-gray-200 items-center justify-center text-sm font-semibold text-gray-600 hidden sm:flex">
            {(user?.email || "?")[0].toUpperCase()}
          </div>
        )}
        <Button variant="outline" size="sm" onClick={doLogout} data-testid="logout-btn" title="Abmelden" className="px-2 sm:px-3">
          <LogOut className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Abmelden</span>
        </Button>
      </div>
    </header>
  );
}

export default function Shell() {
  return (
    <CrawlerProvider>
      <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
        <HeaderBar />
        <div className="flex-1 min-h-0 overflow-hidden">
          <Outlet />
        </div>
      </div>
    </CrawlerProvider>
  );
}
